import { onMounted, type Ref } from 'vue'

export interface UseTextareaAutosizeOptions {
  /** Stop growing after this many visible lines; the textarea scrolls beyond it. */
  maxRows?: number
}

export interface UseTextareaAutosize {
  /** Re-measures and applies the height, returning how many lines the content occupies; it clears the applied height first, so the count can shrink. */
  resize: () => number
}

export function useTextareaAutosize(
  el: Ref<HTMLTextAreaElement | null>,
  options: UseTextareaAutosizeOptions = {},
): UseTextareaAutosize {
  function lineHeightPx(styles: CSSStyleDeclaration): number {
    const parsed = Number.parseFloat(styles.lineHeight)
    return Number.isNaN(parsed) ? Number.parseFloat(styles.fontSize) * 1.2 : parsed
  }

  function ceilingPx(textarea: HTMLTextAreaElement): number | null {
    if (!options.maxRows) return null
    const styles = getComputedStyle(textarea)
    const lineHeight = lineHeightPx(styles)
    const vertical =
      styles.boxSizing === 'border-box'
        ? Number.parseFloat(styles.paddingTop) +
          Number.parseFloat(styles.paddingBottom) +
          Number.parseFloat(styles.borderTopWidth) +
          Number.parseFloat(styles.borderBottomWidth)
        : 0
    return lineHeight * options.maxRows + vertical
  }

  function resize(): number {
    const textarea = el.value
    if (!textarea) return 1
    // The transition kill, start-height rewrite and `void offsetHeight` reflow are load-bearing: a live height transition stops the box collapsing.
    const previous = textarea.offsetHeight
    textarea.style.transition = 'none'
    textarea.style.height = 'auto'
    const content = textarea.scrollHeight
    const styles = getComputedStyle(textarea)
    const lines = Math.max(
      1,
      Math.round(
        (content - Number.parseFloat(styles.paddingTop) - Number.parseFloat(styles.paddingBottom)) /
          lineHeightPx(styles),
      ),
    )
    const ceiling = ceilingPx(textarea)
    const capped = ceiling !== null && content > ceiling
    const target = capped ? ceiling : content
    textarea.style.overflowY = capped ? 'auto' : 'hidden'
    textarea.style.height = `${previous}px`
    void textarea.offsetHeight
    textarea.style.transition = ''
    textarea.style.height = `${target}px`
    return lines
  }

  onMounted(resize)

  return { resize }
}
